import { useCallback, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useQuery } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import type { NavigateFunction } from "react-router-dom";
import toast from "react-hot-toast";

import { updateTenant } from "@/services/authService";
import type { TenantCurrentResponse } from "@/services/authService";
import {
  createTenantInvite,
  listTenantPendingInvites,
  listTenantMembers,
} from "@/services/tenantUsersService";
import { tenantMemberKeys } from "@/lib/queryKeys";
import type { WhoSetupChoice } from "@/pages/onboarding/WhoSetupStep";
import {
  readAccountantInviteEmailFromSession,
  clearAccountantInviteEmailFromSession,
  writeAccountantInviteEmailToSession,
} from "@/pages/onboarding/onboardingSessionState";

const PENDING_INVITES_QUERY_KEY = ["tenant-pending-invites"] as const;

interface UseAccountantHandoffParams {
  tenant: TenantCurrentResponse | null;
  isAccountantFlowOwner: boolean;
  refreshUser: () => Promise<void>;
  navigate: NavigateFunction;
  t: TFunction;
  whoSetupChoice: WhoSetupChoice | null;
  setSavingWhoSetup: (value: boolean) => void;
  loadTenant: () => Promise<void>;
  setViewStep: Dispatch<SetStateAction<number>>;
}

/**
 * Owns the "my accountant continues" handoff: the accountant email captured on
 * WhoSetup, the pending-invite / member queries that gate the owner-facing
 * invite step, and finalizeAccountantHandoff — sending the invite once the plan
 * gate resolves and dropping the owner onto the dashboard.
 *
 * `finalizeAccountantHandoff` is consumed by usePlanGate (its plan_payment=done
 * effect) and by the step-sync effect in useOnboarding, so this hook is wired
 * BEFORE usePlanGate and the callback is passed down.
 */
export function useAccountantHandoff({
  tenant,
  isAccountantFlowOwner,
  refreshUser,
  navigate,
  t,
  whoSetupChoice,
  setSavingWhoSetup,
  loadTenant,
  setViewStep,
}: UseAccountantHandoffParams) {
  /** Accountant email captured on WhoSetup for the "my accountant continues"
   *  path. Sent as a tenant invite only after the plan gate resolves
   *  (finalizeAccountantHandoff). Mirrored to sessionStorage so it survives
   *  the dLocal Go redirect on paid plans. */
  const [accountantInviteEmail, setAccountantInviteEmail] = useState("");

  /** Guards finalizeAccountantHandoff so the invite is sent exactly once per
   *  mount, no matter how many paths race to it (free inline, paid dLocal
   *  return, or the step-sync reload safety net). */
  const accountantHandoffStartedRef = useRef(false);

  const { data: pendingInvites = [] } = useQuery({
    queryKey: [...PENDING_INVITES_QUERY_KEY, tenant?.id],
    queryFn: () => listTenantPendingInvites(tenant!.id),
    enabled: Boolean(tenant?.id && isAccountantFlowOwner),
  });
  const { data: members = [] } = useQuery({
    queryKey: tenantMemberKeys.list(tenant?.id ?? ""),
    queryFn: () => listTenantMembers(tenant!.id),
    enabled: Boolean(tenant?.id && isAccountantFlowOwner),
  });
  const hasAccountantMember = members.some((m) => m.role === "accountant");

  /**
   * Accountant-handoff completion: the owner has chosen "my accountant
   * continues" and just resolved the plan gate (free saved inline, or paid
   * confirmed via dLocal Go return). Send the accountant invite using the
   * email captured on WhoSetup, then drop the owner into the app — they're
   * done; the accountant finishes onboarding from their cartera (WS3).
   *
   * Invite send is best-effort: if it fails the owner still lands on the
   * dashboard, where the handoff panel (WS2) lets them re-invite.
   */
  const finalizeAccountantHandoff = useCallback(async () => {
    const tid = tenant?.id;
    if (!tid) return;
    if (accountantHandoffStartedRef.current) return;
    accountantHandoffStartedRef.current = true;
    // Read from sessionStorage (always written by handleWhoSetupSubmit on the
    // accountant path) rather than the live `accountantInviteEmail` state —
    // this keeps the callback stable so it doesn't re-run the step-sync effect
    // on every keystroke, and it survives the dLocal Go redirect.
    const email = readAccountantInviteEmailFromSession(tid) ?? "";
    if (email) {
      try {
        await createTenantInvite(tid, {
          email: email.toLowerCase(),
          role: "accountant",
        });
        toast.success(t("onboarding.inviteStep.inviteSuccess"));
      } catch {
        toast.error(t("onboarding.inviteStep.inviteError"));
      }
      clearAccountantInviteEmailFromSession(tid);
    }
    await refreshUser();
    navigate("/dashboard", { replace: true });
  }, [tenant?.id, t, refreshUser, navigate]);

  /**
   * WhoSetup step submit: persist the chosen onboarding mode. On the accountant
   * path, stash the invite email to sessionStorage first (it must survive the
   * dLocal Go redirect on paid plans; finalizeAccountantHandoff sends it once the
   * plan resolves). Then advance to the situation/plan step.
   */
  const handleWhoSetupSubmit = useCallback(async () => {
    if (whoSetupChoice === null) return;
    setSavingWhoSetup(true);
    try {
      await updateTenant({ onboardingMode: whoSetupChoice });
      if (whoSetupChoice === "accountant" && tenant?.id) {
        const email = accountantInviteEmail.trim();
        if (email) {
          writeAccountantInviteEmailToSession(tenant.id, email);
        }
      }
      await loadTenant();
      await refreshUser();
      setViewStep(2);
    } catch {
      toast.error(t("onboarding.errorSave"));
    } finally {
      setSavingWhoSetup(false);
    }
  }, [
    whoSetupChoice,
    accountantInviteEmail,
    tenant?.id,
    loadTenant,
    refreshUser,
    setSavingWhoSetup,
    setViewStep,
    t,
  ]);

  return {
    accountantInviteEmail,
    setAccountantInviteEmail,
    pendingInvites,
    members,
    hasAccountantMember,
    finalizeAccountantHandoff,
    handleWhoSetupSubmit,
  };
}
